// src/hooks/useDominiosNovos.js
// Sessão 5.0.1 — v2: detecta similaridade com empresas já cadastradas.
//
// Evolução principal sobre a v1:
//   - Normaliza nomes (sem acento, sufixo S.A./LTDA, caixa, espaços, etc)
//   - Para cada domínio, verifica se o nome sugerido bate (exato ou substring)
//     com alguma empresa existente -> retorna candidatos_match[]
//   - Heurística de sugestão de nome mais esperta (detecta sufixos conhecidos)
//   - Inclui também nome derivado do company_name E do empresa (curto)
//
// Retorno enriquecido por domínio:
//   { dominio, qtd_emails, qtd_from, qtd_to, qtd_cc, ultimo_email,
//     dias_desde_ultimo, sugestao_nome, sugestao_empresa_curto,
//     sugestao_tipo, candidatos_match: [{empresa, confianca}] }

import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

// ============================================================================
// LISTA-NEGRA (domínios que não devem ser sugeridos)
// ============================================================================

const DOMINIOS_PESSOAIS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'hotmail.com.br',
  'yahoo.com', 'yahoo.com.br', 'uol.com.br', 'terra.com.br', 'icloud.com',
  'bol.com.br', 'ig.com.br', 'live.com', 'me.com', 'msn.com', 'google.com',
]);

// Palavras comerciais comumente adicionadas ao final do nome de empresas
// — removemos na normalização para detecção de similaridade.
const SUFIXOS_COMERCIAIS = [
  'sa', 's/a', 'ltda', 'ltd', 'inc', 'corp', 'me', 'eireli',
  'incorporadora', 'empreendimentos', 'engenharia', 'urbanismo',
  'construcao', 'construcoes', 'construtora', 'properties',
  'investimentos', 'invest', 'group', 'holdings', 'realty', 'capital',
  'participacoes',
];

// Dicionário para siglas conhecidas (sobrescreve a heurística padrão)
const SIGLAS_CONHECIDAS = {
  'aam': 'AAM', 'gic': 'GIC', 'hcm': 'HCM', 'gfc': 'GFC', 'mcba': 'MCBA',
  'vbd': 'VBD Law', 'vbdlaw': 'VBD Law', 'hsi': 'HSI', 'jalgp': 'JALGP',
  'aw': 'AW', 'awrealty': 'AW Realty', 'xp': 'XP',
};

// ============================================================================
// HEURÍSTICA DE SUGESTÃO DE NOME
// ============================================================================

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

// "brookfieldproperties.com" → "Brookfield Properties"
// "aamincorporadora.com.br"  → "AAM" (via sigla conhecida)
// "trisul.com.br"            → "Trisul"
const sugerirNome = (dominio) => {
  if (!dominio) return '';
  const raiz = dominio
    .replace(/\.com\.br$/, '').replace(/\.com$/, '')
    .replace(/\.net$/, '').replace(/\.org$/, '').replace(/\.br$/, '')
    .split('.')[0]
    .toLowerCase();

  // 1. Sigla conhecida?
  if (SIGLAS_CONHECIDAS[raiz]) return SIGLAS_CONHECIDAS[raiz];

  // 2. Detectar sufixo composto (ex: brookfield+properties)
  for (const sufixo of SUFIXOS_COMERCIAIS) {
    if (raiz.endsWith(sufixo) && raiz.length > sufixo.length + 2) {
      const core = raiz.slice(0, -sufixo.length);
      if (SIGLAS_CONHECIDAS[core]) {
        return `${SIGLAS_CONHECIDAS[core]} ${capitalize(sufixo)}`;
      }
      return `${capitalize(core)} ${capitalize(sufixo)}`;
    }
  }

  // 3. Capitalize simples
  return capitalize(raiz);
};

// Sugestão de `empresa` (campo curto exibido no Dashboard).
// Igual ao nome sugerido, mas sem sufixos comerciais para ficar enxuto.
const sugerirCurto = (dominio) => {
  const nomeCompleto = sugerirNome(dominio);
  // Remove sufixos comerciais do fim
  const partes = nomeCompleto.split(' ');
  const partesEnxutas = [];
  for (let i = 0; i < partes.length; i++) {
    const eh_sufixo = SUFIXOS_COMERCIAIS.includes(partes[i].toLowerCase());
    const resto_tambem_sufixo = partes.slice(i).every(p =>
      SUFIXOS_COMERCIAIS.includes(p.toLowerCase())
    );
    if (eh_sufixo && resto_tambem_sufixo) break;
    partesEnxutas.push(partes[i]);
  }
  return partesEnxutas.join(' ') || nomeCompleto;
};

// Heurística de tipo_empresa baseada no domínio
const sugerirTipoEmpresa = (dominio) => {
  const d = dominio.toLowerCase();
  if (/\.adv\.br$|^adv\.|law|advog|juridic|vbdlaw/.test(d)) return 'law_firm';
  if (/arquitet|archit|arch\./.test(d))                      return 'architecture';
  if (/engenh|enge\./.test(d))                                return 'engineering';
  if (/cartor|notar|tabeli/.test(d))                          return 'notary';
  if (/consult|gaviao/.test(d))                               return 'consulting';
  if (/contab|contad|audit|kpmg|deloitte|pwc/.test(d))        return 'accounting';
  if (/realty|real-estate|corretora/.test(d))                 return 'real_estate_broker';
  if (/invest|fund|capital|asset/.test(d))                    return 'investment_fund';
  if (/construt|patriani/.test(d))                            return 'builder';
  return 'developer'; // padrão para o domínio do negócio
};

// ============================================================================
// NORMALIZAÇÃO PARA FUZZY MATCH
// ============================================================================

// "BROOKFIELD PROPERTIES" → "brookfieldproperties"
// "TRISUL SA" → "trisul"
// "AAM Incorporadora" → "aam"  (remove sufixo comercial)
const normalizar = (s) => {
  if (!s) return '';
  let out = s.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''); // remove acentos

  // Remove sufixos comerciais (palavras soltas no final)
  const palavras = out.split(/\s+/).filter(Boolean);
  while (palavras.length > 1 && SUFIXOS_COMERCIAIS.includes(palavras[palavras.length - 1])) {
    palavras.pop();
  }
  out = palavras.join('');

  // Remove pontuação e espaços
  return out.replace(/[^a-z0-9]/g, '');
};

// Retorna confiança 0-1 de que os dois nomes se referem à mesma empresa.
// Retorna confiança 0-1 de que os dois nomes se referem à mesma empresa.
const calcularSimilaridade = (a, b) => {
  const na = normalizar(a);
  const nb = normalizar(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1.0;                          // match exato após normalização
  if (na.length >= 4 && nb.includes(na)) return 0.85; // sugestão é substring da existente
  if (nb.length >= 4 && na.includes(nb)) return 0.85; // existente é substring da sugestão
  // 5.1.1: siglas (3 chars em prefixo)
  if (nb.length === 3 && na.startsWith(nb)) return 0.75; // sigla conhecida é prefix (HSI → hsinvest)
  if (na.length === 3 && nb.startsWith(na)) return 0.75;
  // 5.1.1: siglas em final (ex: "investhsi" ~ "HSI")
  if (nb.length === 3 && na.endsWith(nb)) return 0.70;
  return 0;
};

// Para uma lista de empresas, retorna as que batem com `nomeSugerido` ordenadas por confiança
const encontrarCandidatos = (nomeSugerido, empresas, limite = 3) => {
  const out = [];
  for (const emp of empresas) {
    // Comparar com company_name E com empresa (curto), pegar maior
    const s1 = calcularSimilaridade(nomeSugerido, emp.company_name);
    const s2 = calcularSimilaridade(nomeSugerido, emp.empresa);
    const confianca = Math.max(s1, s2);
    if (confianca > 0) {
      out.push({ empresa: emp, confianca });
    }
  }
  return out
    .sort((a, b) => b.confianca - a.confianca)
    .slice(0, limite);
};

// ============================================================================
// HOOK
// ============================================================================

export function useDominiosNovos({ minEmails = 2 } = {}) {
  const [dominios, setDominios] = useState([]);
  const [empresasExistentes, setEmpresasExistentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let ativo = true;

    (async () => {
      setLoading(true);
      setErro(null);

      try {
        const [
          dominiosCadastrados,
          gruposAston,
          todosEmails,
          empresas,
        ] = await Promise.all([
          supabase.from('dominios_empresa').select('dominio'),
          supabase.from('empresas_grupo_aston').select('dominios_email').eq('ativa', true),
          supabase
            .from('emails_gmail')
            .select('from_email, to_emails, cc_emails, data_envio')
            .neq('acao_humana', 'lixo'),
          supabase.from('empresas').select('id, company_name, empresa').order('company_name'),
        ]);

        if (dominiosCadastrados.error) throw dominiosCadastrados.error;
        if (gruposAston.error) throw gruposAston.error;
        if (todosEmails.error) throw todosEmails.error;
        if (empresas.error) throw empresas.error;

        // Lista-negra
        const blacklist = new Set([
          ...DOMINIOS_PESSOAIS,
          ...dominiosCadastrados.data.map(d => d.dominio?.toLowerCase()).filter(Boolean),
          ...gruposAston.data.flatMap(g => g.dominios_email || [])
                             .map(d => d?.toLowerCase()).filter(Boolean),
        ]);

        // Agregação por domínio
        const mapa = new Map();
        const registrar = (dominio, origem, data_envio) => {
          if (!dominio || blacklist.has(dominio)) return;
          let r = mapa.get(dominio);
          if (!r) {
            r = { dominio, qtd_emails: 0, qtd_from: 0, qtd_to: 0, qtd_cc: 0, ultimo_email: null };
            mapa.set(dominio, r);
          }
          r.qtd_emails += 1;
          if (origem === 'from') r.qtd_from += 1;
          if (origem === 'to')   r.qtd_to   += 1;
          if (origem === 'cc')   r.qtd_cc   += 1;
          if (data_envio) {
            const d = new Date(data_envio);
            if (!r.ultimo_email || d > r.ultimo_email) r.ultimo_email = d;
          }
        };

        const extrair = (addr) => {
          if (!addr || typeof addr !== 'string') return null;
          const at = addr.lastIndexOf('@');
          return at > 0 ? addr.slice(at + 1).toLowerCase().trim() : null;
        };

        for (const em of todosEmails.data || []) {
          registrar(extrair(em.from_email), 'from', em.data_envio);
          (em.to_emails || []).forEach(to => registrar(extrair(to), 'to', em.data_envio));
          (em.cc_emails || []).forEach(cc => registrar(extrair(cc), 'cc', em.data_envio));
        }

        const hoje = new Date();
        const empresasData = empresas.data || [];

        const resultado = Array.from(mapa.values())
          .filter(d => d.qtd_emails >= minEmails)
          .map(d => {
            const nomeSugerido   = sugerirNome(d.dominio);
            const curtoSugerido  = sugerirCurto(d.dominio);
            const tipoSugerido   = sugerirTipoEmpresa(d.dominio);
            const candidatosMatch = encontrarCandidatos(nomeSugerido, empresasData);

            return {
              ...d,
              dias_desde_ultimo: d.ultimo_email
                ? Math.floor((hoje - d.ultimo_email) / 86400000) : null,
              sugestao_nome: nomeSugerido,
              sugestao_empresa_curto: curtoSugerido,
              sugestao_tipo: tipoSugerido,
              candidatos_match: candidatosMatch, // [{empresa: {id, company_name, empresa}, confianca}]
            };
          })
          .sort((a, b) => {
            if (b.qtd_emails !== a.qtd_emails) return b.qtd_emails - a.qtd_emails;
            const da = a.ultimo_email?.getTime() || 0;
            const db = b.ultimo_email?.getTime() || 0;
            return db - da;
          });

        if (ativo) {
          setDominios(resultado);
          setEmpresasExistentes(empresasData);
        }
      } catch (err) {
        if (ativo) setErro(err);
      } finally {
        if (ativo) setLoading(false);
      }
    })();

    return () => { ativo = false; };
  }, [minEmails, refreshKey]);

  const refresh = () => setRefreshKey(k => k + 1);

  return { dominios, empresasExistentes, loading, erro, refresh };
}
